// Descargar una respuesta de Chatbularis como Word o PDF.
//
// El asistente NO crea archivos: escribe texto, y ese texto se guarda en la
// conversación. Antes de esto, cuando un abogado pedía "el escrito en Word",
// el modelo a veces contestaba que lo había creado —y no había nada que
// descargar, porque no existe ningún lugar donde pudiera haberlo dejado—. Lo
// que sí existe es el texto, así que el archivo se arma ACÁ, en el navegador,
// a partir del markdown de la respuesta.
//
// Se arma en el navegador y no en el servidor a propósito: el escrito ya está
// en pantalla, y mandarlo de vuelta para que el servidor lo empaquete sería
// un viaje de ida y vuelta de cuarenta kilobytes para hacer algo que no
// necesita ni la base ni una clave. Y no gasta saldo: no hay llamada al modelo.
//
// Los dos formatos parten del mismo árbol de markdown (el que produce
// `marked`), para que el Word y el PDF de una misma respuesta digan lo mismo
// con la misma estructura: títulos, negritas, listas, tablas.
import { marked, type Token, type Tokens } from 'marked';
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import jsPDF from 'jspdf';
import { soloElEscrito } from './limpiarEscrito';
import { FORMATO_ESCRITO_DEFAULT } from './asistente';

export type FormatoEscrito = 'docx' | 'pdf';

/** Cómo se arma el archivo: la letra, el tamaño, el interlineado y los
 *  márgenes del estudio (Configuración de Chatbularis, "Modelos y estilo"), y
 *  el nombre del estudio como autor del documento. Si no llega nada, Times 12
 *  a espacio y medio con 2,5 cm. */
export type OpcionesEscrito = {
  fuente?: string;
  tamano?: number;
  interlineado?: number;
  margenCm?: number;
  estudio?: string;
};

const OPCIONES_DEFAULT: Required<OpcionesEscrito> = { ...FORMATO_ESCRITO_DEFAULT, estudio: '' };

/** Un tramo de texto con su formato. Es lo único que viaja del markdown a los
 *  dos generadores: cada uno decide cómo pintar "negrita" en su formato. */
type Tramo = { texto: string; negrita?: boolean; cursiva?: boolean; codigo?: boolean };


/* ── Nombre del archivo ──────────────────────────────────────────────── */

/** El nombre sale del primer título de la respuesta o, si no hay, de sus
 *  primeras palabras: "demanda-laboral-despido-injustificado-2026-09-14.docx"
 *  se reconoce en la carpeta de descargas; "chatbularis (3).docx" no. */
export function nombreDeArchivo(markdown: string, formato: FormatoEscrito): string {
  const tokens = marked.lexer(markdown);
  const titulo = tokens.find((t): t is Tokens.Heading => t.type === 'heading');
  const base = titulo ? textoPlano(titulo.tokens) : textoPlano(tokensDe(tokens[0])).slice(0, 80);
  const slug = base
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  const fecha = new Date().toISOString().slice(0, 10);
  return `${slug || 'chatbularis'}-${fecha}.${formato}`;
}

/* ── Del markdown a tramos ───────────────────────────────────────────── */

/** Los hijos de un token de bloque, si los tiene. */
function tokensDe(t: Token | undefined): Token[] {
  if (!t) return [];
  const conHijos = t as { tokens?: Token[] };
  return conHijos.tokens ?? [];
}

/** Aplana los tokens de línea (negrita, cursiva, enlaces…) en tramos. */
function tramosDe(tokens: Token[] | undefined, base: Omit<Tramo, 'texto'> = {}): Tramo[] {
  const salida: Tramo[] = [];
  for (const t of tokens ?? []) {
    switch (t.type) {
      case 'strong':
        salida.push(...tramosDe((t as Tokens.Strong).tokens, { ...base, negrita: true }));
        break;
      case 'em':
        salida.push(...tramosDe((t as Tokens.Em).tokens, { ...base, cursiva: true }));
        break;
      case 'del':
        salida.push(...tramosDe((t as Tokens.Del).tokens, base));
        break;
      case 'codespan':
        salida.push({ ...base, texto: (t as Tokens.Codespan).text, codigo: true });
        break;
      case 'link': {
        // El texto del enlace y, si no es el mismo, la dirección entre
        // paréntesis: en papel un enlace sin su dirección no lleva a ninguna
        // parte.
        const l = t as Tokens.Link;
        const texto = textoPlano(l.tokens);
        salida.push({ ...base, texto: texto === l.href ? texto : `${texto} (${l.href})` });
        break;
      }
      case 'br':
        salida.push({ ...base, texto: '\n' });
        break;
      case 'escape':
        salida.push({ ...base, texto: (t as Tokens.Escape).text });
        break;
      case 'image':
        break;
      case 'text': {
        const tx = t as Tokens.Text;
        if (tx.tokens?.length) salida.push(...tramosDe(tx.tokens, base));
        else salida.push({ ...base, texto: desescapar(tx.text) });
        break;
      }
      default:
        salida.push({ ...base, texto: desescapar((t as { text?: string; raw?: string }).text ?? (t as { raw?: string }).raw ?? '') });
    }
  }
  return salida;
}

/** marked deja algunas entidades HTML en el texto; en un .docx o un PDF
 *  "&amp;" se leería tal cual. */
function desescapar(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

const textoPlano = (tokens: Token[] | undefined) => tramosDe(tokens).map((r) => r.texto).join('');

/* ── Word ────────────────────────────────────────────────────────────── */

/** Twips: 1/20 de punto. 720 twips = media pulgada, la sangría estándar. */
const SANGRIA = 720;

const NIVELES: Record<number, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  1: HeadingLevel.HEADING_1,
  2: HeadingLevel.HEADING_2,
  3: HeadingLevel.HEADING_3,
  4: HeadingLevel.HEADING_4,
  5: HeadingLevel.HEADING_5,
  6: HeadingLevel.HEADING_6,
};

function runsWord(tramos: Tramo[]): TextRun[] {
  const runs: TextRun[] = [];
  for (const r of tramos) {
    // Un salto de línea dentro de un párrafo (dos espacios al final) es un
    // `break` en Word, no un párrafo nuevo.
    const partes = r.texto.split('\n');
    partes.forEach((p, i) => {
      runs.push(
        new TextRun({
          text: p,
          bold: r.negrita,
          italics: r.cursiva,
          font: r.codigo ? 'Courier New' : undefined,
          break: i > 0 ? 1 : undefined,
        }),
      );
    });
  }
  return runs;
}

/** Recorre los bloques y devuelve los párrafos y tablas del documento.
 *
 *  Las listas numeradas llevan cada una su propia instancia de numeración:
 *  sin eso, la segunda lista del escrito seguiría contando desde donde quedó
 *  la primera, y las "Peticiones" empezarían en 7. */
function bloquesWord(tokens: Token[], instancia: { n: number }, nivel = 0): (Paragraph | Table)[] {
  const salida: (Paragraph | Table)[] = [];
  for (const t of tokens) {
    switch (t.type) {
      case 'heading': {
        const h = t as Tokens.Heading;
        salida.push(new Paragraph({ heading: NIVELES[Math.min(h.depth, 6)], children: runsWord(tramosDe(h.tokens)) }));
        break;
      }
      case 'paragraph':
        salida.push(new Paragraph({ children: runsWord(tramosDe((t as Tokens.Paragraph).tokens)), alignment: AlignmentType.JUSTIFIED, spacing: { after: 160 } }));
        break;
      case 'text':
        // Dentro de una lista apretada el texto viene suelto, sin párrafo.
        salida.push(new Paragraph({ children: runsWord(tramosDe((t as Tokens.Text).tokens ?? [t])), spacing: { after: 80 } }));
        break;
      case 'list': {
        const l = t as Tokens.List;
        const ordenada = l.ordered;
        const instanciaLista = ordenada ? instancia.n++ : 0;
        for (const item of l.items) {
          // El texto propio del ítem va con la viñeta; lo demás (listas
          // anidadas, párrafos siguientes) va debajo, un nivel más adentro.
          const propios = item.tokens.filter((x) => x.type !== 'list');
          const anidados = item.tokens.filter((x) => x.type === 'list');
          const tramos = propios.flatMap((x) => tramosDe(tokensDe(x).length ? tokensDe(x) : [x]));
          salida.push(
            new Paragraph({
              children: runsWord(tramos),
              spacing: { after: 60 },
              ...(ordenada
                ? { numbering: { reference: 'numerada', level: Math.min(nivel, 2), instance: instanciaLista } }
                : { bullet: { level: Math.min(nivel, 2) } }),
            }),
          );
          for (const sub of anidados) salida.push(...bloquesWord([sub], instancia, nivel + 1));
        }
        break;
      }
      case 'table': {
        const tb = t as Tokens.Table;
        const celda = (c: Tokens.TableCell, encabezado: boolean) =>
          new TableCell({
            children: [new Paragraph({ children: runsWord(tramosDe(c.tokens).map((r) => (encabezado ? { ...r, negrita: true } : r))) })],
            shading: encabezado ? { fill: 'EEEEEE' } : undefined,
          });
        salida.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({ tableHeader: true, children: tb.header.map((c) => celda(c, true)) }),
              ...tb.rows.map((fila) => new TableRow({ children: fila.map((c) => celda(c, false)) })),
            ],
          }),
        );
        salida.push(new Paragraph({ spacing: { after: 120 } }));
        break;
      }
      case 'blockquote':
        for (const p of bloquesWord((t as Tokens.Blockquote).tokens, instancia, nivel)) {
          if (p instanceof Paragraph) salida.push(new Paragraph({ children: [], indent: { left: SANGRIA } }));
          salida.push(p);
        }
        break;
      case 'code':
        for (const linea of (t as Tokens.Code).text.split('\n')) {
          salida.push(new Paragraph({ children: [new TextRun({ text: linea, font: 'Courier New', size: 20 })], spacing: { after: 0 } }));
        }
        salida.push(new Paragraph({ spacing: { after: 120 } }));
        break;
      case 'hr':
        salida.push(new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '999999', space: 1 } }, spacing: { after: 200 } }));
        break;
      case 'html':
        salida.push(new Paragraph({ children: [new TextRun((t as Tokens.HTML).text)] }));
        break;
      case 'space':
        break;
      default:
        break;
    }
  }
  return salida;
}

/** Arma el .docx. Times New Roman 12 es la letra del foro chileno; los títulos
 *  van en negro y no en el azul que Word trae por defecto, porque un escrito
 *  judicial no lleva colores. */
export async function aWord(markdown: string, opciones: OpcionesEscrito = {}): Promise<Blob> {
  const o = { ...OPCIONES_DEFAULT, ...opciones };
  const tokens = marked.lexer(markdown);
  // docx mide la letra en medios puntos y el interlineado en 240avos de línea.
  const tam = o.tamano * 2;
  const linea = Math.round(o.interlineado * 240);
  const margen = Math.round(o.margenCm * 567);
  const titulo = (size: number, extra: object = {}) => ({ size, bold: true, color: '000000', font: o.fuente, ...extra });
  const doc = new Document({
    // El autor del archivo es el estudio, no el asistente: un Word que diga
    // "creado por Chatbularis" en sus propiedades delata lo mismo que una
    // frase de chat adentro.
    creator: o.estudio || 'Tabularis',
    // Los títulos van DENTRO de `default`, que reemplaza los de la librería.
    // Como `paragraphStyles` se sumaban a los de fábrica (azules, sin
    // negrita, en 16 puntos) con el mismo nombre, y Word se queda con el
    // primero: los títulos salían sin el formato del estudio.
    styles: {
      default: {
        document: { run: { font: o.fuente, size: tam }, paragraph: { spacing: { line: linea } } },
        heading1: { run: titulo(tam + 6), paragraph: { spacing: { before: 320, after: 160, line: linea }, alignment: AlignmentType.CENTER } },
        heading2: { run: titulo(tam + 2), paragraph: { spacing: { before: 280, after: 120, line: linea } } },
        heading3: { run: titulo(tam), paragraph: { spacing: { before: 200, after: 100, line: linea } } },
        heading4: { run: titulo(tam, { italics: true }), paragraph: { spacing: { before: 160, after: 80, line: linea } } },
        heading5: { run: titulo(tam), paragraph: { spacing: { before: 120, after: 60, line: linea } } },
        heading6: { run: { size: tam, italics: true, color: '000000', font: o.fuente }, paragraph: { spacing: { before: 120, after: 60, line: linea } } },
      },
      // La librería no trae "Normal" y los títulos se basan en él: sin
      // definirlo, cada programa lo arma a su manera (Calibri 11, Aptos…).
      paragraphStyles: [{ id: 'Normal', name: 'Normal', quickFormat: true, run: { font: o.fuente, size: tam }, paragraph: { spacing: { line: linea } } }],
    },
    numbering: {
      config: [
        {
          reference: 'numerada',
          levels: [0, 1, 2].map((level) => ({
            level,
            format: 'decimal' as const,
            text: `%${level + 1}.`,
            alignment: AlignmentType.START,
            style: { paragraph: { indent: { left: SANGRIA * (level + 1), hanging: 360 } } },
          })),
        },
      ],
    },
    sections: [
      {
        properties: { page: { margin: { top: margen, bottom: margen, left: margen, right: margen } } },
        children: bloquesWord(tokens, { n: 1 }),
      },
    ],
  });
  return Packer.toBlob(doc);
}

/* ── PDF ─────────────────────────────────────────────────────────────── */

/** Lo que la Helvetica de jsPDF sabe dibujar: Latin-1 más los signos de
 *  WinAnsi (rayas, comillas tipográficas, viñeta, puntos suspensivos, euro).
 *  Todo lo demás —emojis, el ⚠️ del pie, las líneas de caja— saldría como un
 *  cuadrado o una letra equivocada, así que se quita antes de dibujar. */
const EXTRAS_WINANSI = new Set('–—‘’‚“”„•…‰€™‹›ˆ˜ŠšŽžŒœŸƒ'.split(''));
function dibujable(s: string): string {
  let r = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp <= 0xff || EXTRAS_WINANSI.has(ch)) r += ch;
    else if (cp === 0x2500 || cp === 0x2550) r += '-';
    // Los demás se omiten (emojis y variantes). Sin espacio de reemplazo: un
    // "?" en medio de un escrito parece una errata del abogado.
  }
  return r.replace(/  +/g, ' ');
}

type Fuente = 'normal' | 'bold' | 'italic' | 'bolditalic';
const fuenteDe = (r: Tramo): Fuente => (r.negrita && r.cursiva ? 'bolditalic' : r.negrita ? 'bold' : r.cursiva ? 'italic' : 'normal');

/** Un escritor de párrafos con tramos de distinto formato, con corte de línea
 *  por palabra y salto de página cuando toca.
 *
 *  jsPDF sabe cortar texto de UNA fuente (`splitTextToSize`); acá cada línea
 *  puede mezclar normal y negrita, así que el corte se hace a mano midiendo
 *  palabra por palabra con la fuente que le toca. */
/** jsPDF trae tres familias de fábrica: serif (times), sans (helvetica) y
 *  monoespaciada. La letra del estudio se traduce a la más parecida: Times,
 *  Cambria y Garamond son serif; Arial, Calibri y Verdana, sans. El Word sí
 *  lleva la letra exacta. Exportada para decirlo en la pantalla del formato. */
export const familiaPdf = (fuente: string): 'times' | 'helvetica' =>
  /times|cambria|garamond|georgia|serif/i.test(fuente) ? 'times' : 'helvetica';

class Pagina {
  readonly doc: jsPDF;
  readonly ancho: number;
  readonly alto: number;
  readonly margen: number;
  /** Tamaño base del cuerpo, en puntos, y factor de interlínea. */
  readonly tamBase: number;
  readonly interlineado: number;
  readonly familia: 'times' | 'helvetica';
  y: number;

  constructor(o: Required<OpcionesEscrito>) {
    this.doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.ancho = this.doc.internal.pageSize.getWidth();
    this.alto = this.doc.internal.pageSize.getHeight();
    this.margen = o.margenCm * 10;
    this.tamBase = o.tamano;
    this.interlineado = o.interlineado;
    this.familia = familiaPdf(o.fuente);
    this.y = this.margen;
  }

  get anchoUtil() { return this.ancho - this.margen * 2; }

  /** Baja el cursor y, si no cabe lo que viene, pasa de página. */
  bajar(mm: number) {
    if (this.y + mm > this.alto - this.margen) {
      this.doc.addPage();
      this.y = this.margen;
    } else {
      this.y += mm;
    }
  }

  parrafo(tramos: Tramo[], o: { tam?: number; sangria?: number; colgante?: string; familia?: 'helvetica' | 'times' | 'courier'; centrado?: boolean; despues?: number } = {}) {
    const tam = o.tam ?? this.tamBase;
    const familia = o.familia ?? this.familia;
    // Puntos → mm. Una línea a espacio sencillo mide 1,2 veces la letra; el
    // interlineado del estudio multiplica eso.
    const interlinea = tam * 0.3528 * 1.2 * this.interlineado;
    const x0 = this.margen + (o.sangria ?? 0);
    const anchoLinea = this.anchoUtil - (o.sangria ?? 0);

    // Palabras con su fuente. Los espacios se pegan a la palabra anterior
    // para poder medirlos con la misma fuente.
    type Palabra = { texto: string; fuente: Fuente; salto?: boolean };
    const palabras: Palabra[] = [];
    for (const r of tramos) {
      const fuente: Fuente = r.codigo ? 'normal' : fuenteDe(r);
      const texto = dibujable(r.texto);
      const lineas = texto.split('\n');
      lineas.forEach((linea, i) => {
        if (i > 0) palabras.push({ texto: '', fuente, salto: true });
        for (const m of linea.match(/\S+\s*|\s+/g) ?? []) palabras.push({ texto: m, fuente });
      });
    }

    const ancho = (p: Palabra) => {
      this.doc.setFont(familia, p.fuente);
      this.doc.setFontSize(tam);
      return this.doc.getTextWidth(p.texto);
    };

    // Se arman las líneas primero y se dibujan después: así el salto de
    // página se decide por línea entera y no a media palabra.
    const lineas: Palabra[][] = [[]];
    let usado = 0;
    for (const p of palabras) {
      if (p.salto) { lineas.push([]); usado = 0; continue; }
      const w = ancho(p);
      if (usado + w > anchoLinea && lineas[lineas.length - 1].length) {
        lineas.push([]);
        usado = 0;
      }
      lineas[lineas.length - 1].push(p);
      usado += w;
    }

    // Si el párrafo entero no cabe pero cabe una línea, se empieza igual: lo
    // que no se permite es que la primera línea quede sola al pie (viuda).
    if (this.y + interlinea * Math.min(2, lineas.length) > this.alto - this.margen) this.bajar(this.alto);

    lineas.forEach((linea, i) => {
      if (i > 0) this.bajar(interlinea);
      else if (this.y + interlinea > this.alto - this.margen) this.bajar(this.alto);
      let x = x0;
      if (o.colgante && i === 0) {
        this.doc.setFont(familia, 'normal');
        this.doc.setFontSize(tam);
        this.doc.text(o.colgante, x0 - this.doc.getTextWidth(o.colgante) - 1.5, this.y + interlinea * 0.72);
      }
      if (o.centrado) {
        const total = linea.reduce((n, p) => n + ancho(p), 0);
        x = this.margen + (this.anchoUtil - total) / 2;
      }
      for (const p of linea) {
        this.doc.setFont(familia, p.fuente);
        this.doc.setFontSize(tam);
        this.doc.text(p.texto, x, this.y + interlinea * 0.72);
        x += this.doc.getTextWidth(p.texto);
      }
    });
    this.y += interlinea;
    this.bajar(o.despues ?? interlinea * 0.55);
  }

  linea() {
    this.bajar(2);
    this.doc.setDrawColor(150);
    this.doc.setLineWidth(0.2);
    this.doc.line(this.margen, this.y, this.ancho - this.margen, this.y);
    this.bajar(4);
  }

  /** Tabla sencilla: columnas del mismo ancho, texto envuelto, bordes finos. */
  tabla(encabezado: Tokens.TableCell[], filas: Tokens.TableCell[][]) {
    const cols = Math.max(1, encabezado.length);
    const anchoCol = this.anchoUtil / cols;
    // Un poco más chica que el cuerpo, con el interlineado del estudio: igual
    // que en el Word, donde la tabla hereda el estilo del documento.
    const tam = Math.max(8, this.tamBase - 2);
    const interlinea = tam * 0.3528 * 1.2 * this.interlineado;
    const relleno = 1.6;
    const dibujarFila = (celdas: Tokens.TableCell[], negrita: boolean) => {
      this.doc.setFont(this.familia, negrita ? 'bold' : 'normal');
      this.doc.setFontSize(tam);
      const textos = celdas.map((c) => this.doc.splitTextToSize(dibujable(textoPlano(c.tokens)), anchoCol - relleno * 2) as string[]);
      const altoFila = Math.max(1, ...textos.map((t) => t.length)) * interlinea + relleno * 2;
      if (this.y + altoFila > this.alto - this.margen) this.bajar(this.alto);
      this.doc.setDrawColor(170);
      this.doc.setLineWidth(0.2);
      if (negrita) {
        this.doc.setFillColor(238, 238, 238);
        this.doc.rect(this.margen, this.y, anchoCol * cols, altoFila, 'F');
      }
      textos.forEach((lineas, i) => {
        const x = this.margen + anchoCol * i;
        this.doc.rect(x, this.y, anchoCol, altoFila);
        lineas.forEach((l, j) => this.doc.text(l, x + relleno, this.y + relleno + interlinea * (j + 0.72)));
      });
      this.y += altoFila;
    };
    dibujarFila(encabezado, true);
    for (const f of filas) dibujarFila(f, false);
    this.bajar(4);
  }

  /** Número de página al pie de todas, cuando ya se sabe cuántas son. Solo
   *  el número: antes iba también "Chatbularis IA · Borrador para revisión
   *  profesional", y un escrito que se presenta en tribunales no puede llevar
   *  al pie el nombre de un asistente. */
  numerar() {
    const total = this.doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      this.doc.setPage(i);
      this.doc.setFont(this.familia, 'normal');
      this.doc.setFontSize(8.5);
      this.doc.setTextColor(120);
      this.doc.text(`${i}`, this.ancho / 2, this.alto - 12, { align: 'center' });
      this.doc.setTextColor(0);
    }
  }
}

/** Cuánto más grande que el cuerpo va cada nivel de título. */
const MAS_TITULO: Record<number, number> = { 1: 3, 2: 1.5, 3: 0.5, 4: 0, 5: 0, 6: 0 };

function bloquesPdf(pg: Pagina, tokens: Token[], nivel = 0) {
  const sangriaBase = nivel * 7;
  for (const t of tokens) {
    switch (t.type) {
      case 'heading': {
        const h = t as Tokens.Heading;
        pg.bajar(h.depth <= 2 ? 4 : 2.5);
        pg.parrafo(tramosDe(h.tokens).map((r) => ({ ...r, negrita: true })), { tam: pg.tamBase + MAS_TITULO[Math.min(h.depth, 6)], centrado: h.depth === 1, despues: 2.5, sangria: sangriaBase });
        break;
      }
      case 'paragraph':
        pg.parrafo(tramosDe((t as Tokens.Paragraph).tokens), { sangria: sangriaBase });
        break;
      case 'text':
        pg.parrafo(tramosDe((t as Tokens.Text).tokens ?? [t]), { sangria: sangriaBase, despues: 1.5 });
        break;
      case 'list': {
        const l = t as Tokens.List;
        const inicio = typeof l.start === 'number' ? l.start : 1;
        l.items.forEach((item, i) => {
          const propios = item.tokens.filter((x) => x.type !== 'list');
          const anidados = item.tokens.filter((x) => x.type === 'list');
          const tramos = propios.flatMap((x) => tramosDe(tokensDe(x).length ? tokensDe(x) : [x]));
          pg.parrafo(tramos, { sangria: sangriaBase + 7, colgante: l.ordered ? `${inicio + i}.` : '•', despues: 1.5 });
          for (const sub of anidados) bloquesPdf(pg, [sub], nivel + 1);
        });
        pg.bajar(2);
        break;
      }
      case 'table': {
        const tb = t as Tokens.Table;
        pg.tabla(tb.header, tb.rows);
        break;
      }
      case 'blockquote':
        for (const sub of (t as Tokens.Blockquote).tokens) {
          if (sub.type === 'paragraph' || sub.type === 'text') {
            pg.parrafo(tramosDe(tokensDe(sub)).map((r) => ({ ...r, cursiva: true })), { sangria: sangriaBase + 7 });
          } else {
            bloquesPdf(pg, [sub], nivel + 1);
          }
        }
        break;
      case 'code':
        for (const linea of (t as Tokens.Code).text.split('\n')) {
          pg.parrafo([{ texto: linea || ' ' }], { familia: 'courier', tam: 9.5, despues: 0, sangria: sangriaBase });
        }
        pg.bajar(3);
        break;
      case 'hr':
        pg.linea();
        break;
      case 'html':
        pg.parrafo([{ texto: (t as Tokens.HTML).text }], { sangria: sangriaBase });
        break;
      default:
        break;
    }
  }
}

export function aPdf(markdown: string, opciones: OpcionesEscrito = {}): Blob {
  const pg = new Pagina({ ...OPCIONES_DEFAULT, ...opciones });
  bloquesPdf(pg, marked.lexer(markdown));
  pg.numerar();
  return pg.doc.output('blob');
}

/* ── Descarga ────────────────────────────────────────────────────────── */

/** Genera el archivo y lo entrega al navegador como descarga. Lo que se
 *  convierte es SOLO el escrito (ver limpiarEscrito.ts), limpiado una vez
 *  acá para el archivo y para su nombre. */
export async function descargarEscrito(markdown: string, formato: FormatoEscrito, opciones: OpcionesEscrito = {}): Promise<void> {
  const limpio = soloElEscrito(markdown);
  const blob = formato === 'docx' ? await aWord(limpio, opciones) : aPdf(limpio, opciones);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreDeArchivo(limpio, formato);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Se revoca después y no de inmediato: en algunos navegadores la descarga
  // todavía no empezó cuando vuelve `click()`.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
